import React, { forwardRef, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { IconEditAi } from "@purpur/icon/edit-ai";
import c from "classnames/bind";

import type { TextFieldAiInternalProps } from "./text-field";
import aiStyles from "./text-field-ai.module.scss";
import { TextFieldBase } from "./text-field-base";

const cx = c.bind(aiStyles);
const rootClassName = "purpur-text-field-ai";
const defaultTypingAnimationDurationMs = 1400;

const hasInputValue = (value: unknown) => {
  if (typeof value === "number") {
    return !Number.isNaN(value);
  }

  if (typeof value === "string") {
    return value.length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null;
};

export const TextFieldAiInternal = forwardRef<HTMLInputElement, TextFieldAiInternalProps>(
  (
    {
      ["data-testid"]: dataTestId,
      animatePrepopulatedValue = false,
      typingAnimationDurationMs = defaultTypingAnimationDurationMs,
      endAdornment,
      value: controlledValue,
      defaultValue,
      onChange,
      disabled,
      readOnly,
      valid = false,
      negative = false,
      ...baseProps
    },
    ref
  ) => {
    const getTestId = (name: string) => (dataTestId ? `${dataTestId}-${name}` : undefined);
    const isControlled = controlledValue !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const [hasUserEditedValue, setHasUserEditedValue] = useState(false);
    const [shouldAnimatePrepopulatedValue, setShouldAnimatePrepopulatedValue] = useState(false);
    const [hasAnimatedPrepopulatedValue, setHasAnimatedPrepopulatedValue] = useState(false);
    const [animatedTypingValue, setAnimatedTypingValue] = useState<string | null>(null);
    const typingAnimationTimerRef = useRef<number | null>(null);
    const inputContainerRef = useRef<HTMLDivElement | null>(null);

    const value = isControlled ? controlledValue : uncontrolledValue;
    const hasValue = hasInputValue(value);
    const isAiPresentationActive = hasValue && !hasUserEditedValue;
    const isTypingAnimationRunning = animatedTypingValue !== null;
    const displayValue = isTypingAnimationRunning ? animatedTypingValue : value ?? "";
    const shouldAnimatePrepopulated =
      isAiPresentationActive &&
      animatePrepopulatedValue &&
      !hasAnimatedPrepopulatedValue &&
      !disabled &&
      !readOnly;

    const clearTypingAnimation = useCallback(() => {
      if (typingAnimationTimerRef.current !== null) {
        window.clearInterval(typingAnimationTimerRef.current);
        typingAnimationTimerRef.current = null;
      }
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      clearTypingAnimation();
      setAnimatedTypingValue(null);

      if (!isControlled) {
        setUncontrolledValue(event.currentTarget.value);
      }

      setHasUserEditedValue(true);
      onChange?.(event);
    };

    useEffect(() => {
      if (!shouldAnimatePrepopulated || typeof IntersectionObserver === "undefined") {
        return;
      }

      const node = inputContainerRef.current;

      if (!node) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          const isVisible = entries.some((entry) => entry.isIntersecting);

          if (isVisible) {
            setShouldAnimatePrepopulatedValue(true);
            setHasAnimatedPrepopulatedValue(true);
            observer.disconnect();
          }
        },
        { threshold: 0.35 }
      );

      observer.observe(node);

      return () => observer.disconnect();
    }, [shouldAnimatePrepopulated]);

    useEffect(() => {
      if (!shouldAnimatePrepopulatedValue || hasUserEditedValue || typeof value !== "string") {
        return;
      }

      const shouldReduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

      if (shouldReduceMotion || value.length === 0) {
        setShouldAnimatePrepopulatedValue(false);
        return;
      }

      clearTypingAnimation();
      setAnimatedTypingValue("");

      let index = 0;
      const safeAnimationDuration =
        Number.isFinite(typingAnimationDurationMs) && typingAnimationDurationMs > 0
          ? typingAnimationDurationMs
          : defaultTypingAnimationDurationMs;
      const intervalDuration = Math.max(20, Math.floor(safeAnimationDuration / value.length));

      typingAnimationTimerRef.current = window.setInterval(() => {
        index += 1;
        setAnimatedTypingValue(value.slice(0, index));

        if (index >= value.length) {
          clearTypingAnimation();
          setAnimatedTypingValue(null);
          setShouldAnimatePrepopulatedValue(false);
        }
      }, intervalDuration);

      return clearTypingAnimation;
    }, [
      clearTypingAnimation,
      hasUserEditedValue,
      shouldAnimatePrepopulatedValue,
      typingAnimationDurationMs,
      value,
    ]);

    useEffect(() => clearTypingAnimation, [clearTypingAnimation]);

    const aiEndAdornments: ReactNode[] = [];

    if (endAdornment === undefined && isAiPresentationActive) {
      aiEndAdornments.push(
        <IconEditAi
          key="ai-icon"
          size="xs"
          data-testid={getTestId("edit-ai-icon")}
          className={cx(`${rootClassName}__ai-icon`, {
            [`${rootClassName}__ai-icon--negative`]: negative,
          })}
        />
      );
    }

    return (
      <TextFieldBase
        ref={ref}
        data-testid={dataTestId}
        {...baseProps}
        endAdornment={endAdornment}
        value={displayValue}
        onChange={handleInputChange}
        disabled={disabled}
        readOnly={readOnly}
        valid={valid}
        negative={negative}
        _styles={aiStyles}
        _rootClassName={rootClassName}
        _prependEndAdornments={aiEndAdornments}
        _inputModifiers={{ [`${rootClassName}__input--ai`]: isAiPresentationActive }}
        _frameModifiers={{ [`${rootClassName}__frame--ai`]: isAiPresentationActive }}
        _inputContainerRef={inputContainerRef}
      />
    );
  }
);

TextFieldAiInternal.displayName = "TextField";
